import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EntityTag,
  EntityTagWithLink,
  tagsApi,
} from '@texas/api/endpoints/tagsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { request } from '@texas/utils/helpers/httpHelpers';
import { Icons } from '../shared/Icons';
import { CreateTagModal } from './CreateTagModal';
import { UpdateTagModal } from './UpdateTagModal';
import { SharedDisclosureProps } from '../shared/types';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useActiveSession } from '@texas/hooks/useSession';
import { tagEvents } from './events';

export interface EditTagsModalProps extends SharedDisclosureProps {
  entityId: number;
  entityType: EntityType;
  currentTags: Pick<EntityTagWithLink, 'id'>[];
}

export function EditTagLinksModal({
  isOpen,
  onClose,
  entityId,
  entityType,
  currentTags,
}: EditTagsModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const session = useActiveSession();
  const userId = session.currentUser.id;

  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set());

  const {
    isOpen: isCreateOpen,
    onClose: onCreateClose,
    onOpen: onCreateOpen,
  } = useDisclosure();

  const {
    isOpen: isUpdateOpen,
    onClose: onUpdateClose,
    onOpen: onUpdateOpen,
  } = useDisclosure();

  const [editingTagId, setEditingTagId] = useState<number | null>(null);

  const {
    data: allTags,
    refetch: refetchAllTags,
    loading: loadingTags,
    error: tagsError,
  } = useApiResource(tagsApi.getAll);

  const { request: updateLinkedTagsRequest, loading: updating } = useApiRequest(
    tagsApi.updateLinkedTags,
  );

  useEffect(() => {
    if (isOpen) {
      refetchAllTags({
        includeArchived: false,
        includeSystemTags: false,
        filterByEntityType: entityType,
      });
      setSelectedTagIds(new Set(currentTags.map((t) => t.id)));
    }
  }, [isOpen, refetchAllTags, currentTags, entityType]);

  const handleToggleTag = (tagId: number) => {
    setSelectedTagIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  const handleSave = useCallback(async () => {
    const currentTagIds = new Set(currentTags.map((t) => t.id));

    const tagIdsToAdd = [...selectedTagIds].filter(
      (id) => !currentTagIds.has(id),
    );
    const tagIdsToRemove = [...currentTagIds].filter(
      (id) => !selectedTagIds.has(id),
    );

    if (tagIdsToAdd.length === 0 && tagIdsToRemove.length === 0) {
      onClose();
      return;
    }

    await request(
      updateLinkedTagsRequest,
      [
        {
          entityId,
          entityType,
          tagIdsToAdd,
          tagIdsToRemove,
        },
      ],
      () => {
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });
        tagEvents.tagLinksUpdated.dispatch({ entityId, entityType });
        onClose();
      },
      () => {
        toast({
          title: t('general.updateFailed'),
          status: 'error',
          isClosable: true,
        });
      },
    );
  }, [
    currentTags,
    selectedTagIds,
    entityId,
    entityType,
    updateLinkedTagsRequest,
    toast,
    t,
    onClose,
  ]);

  const handleTagCreated = (tag: EntityTag) => {
    refetchAllTags({
      includeArchived: false,
      includeSystemTags: false,
      filterByEntityType: entityType,
    });
    setSelectedTagIds((prev) => new Set([...prev, tag.id]));
  };

  const handleEditTag = useCallback(
    (tagId: number, event: React.MouseEvent) => {
      event.stopPropagation();
      setEditingTagId(tagId);
      onUpdateOpen();
    },
    [onUpdateOpen],
  );

  const handleTagUpdated = useCallback(
    (_updatedTag: EntityTag) => {
      refetchAllTags({
        includeArchived: false,
        includeSystemTags: false,
        filterByEntityType: entityType,
      });
    },
    [refetchAllTags, entityType],
  );

  const isSaving = updating;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('tags.editTags')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingTags && (
              <Flex justifyContent="center" p={4}>
                <Spinner />
              </Flex>
            )}
            {tagsError && <ErrorDetails error={tagsError} />}
            {!loadingTags && allTags && (
              <VStack align="stretch" spacing={2}>
                {allTags.length === 0 ? (
                  <Text variant="sub">{t('tags.noTags')}</Text>
                ) : (
                  allTags.map((tag) => (
                    <Box
                      key={tag.id}
                      p={2}
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => handleToggleTag(tag.id)}
                      bg={
                        selectedTagIds.has(tag.id)
                          ? 'blackAlpha.200'
                          : 'transparent'
                      }
                      _hover={{ bg: 'blackAlpha.200' }}
                    >
                      <Flex
                        alignItems="center"
                        gap={2}
                        justifyContent="space-between"
                      >
                        <Flex alignItems="center" gap={2}>
                          <Checkbox
                            isChecked={selectedTagIds.has(tag.id)}
                            onChange={() => handleToggleTag(tag.id)}
                            pointerEvents="none"
                          />
                          <Tag bg={tag.color} color="white" fontWeight="bold">
                            {tag.label}
                          </Tag>
                        </Flex>
                        {tag.userId === userId && (
                          <Tooltip label={t('tags.edit')}>
                            <IconButton
                              aria-label={t('tags.edit')}
                              size="xs"
                              variant="ghost"
                              icon={<Icons.Pencil />}
                              onClick={(e) => handleEditTag(tag.id, e)}
                            />
                          </Tooltip>
                        )}
                      </Flex>
                    </Box>
                  ))
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Flex gap={2}>
              <Button
                leftIcon={<Icons.Plus />}
                variant="outline"
                onClick={onCreateOpen}
              >
                {t('tags.createNew')}
              </Button>
              <Button
                variant="texas-solid"
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t('general.save')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CreateTagModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onTagCreated={handleTagCreated}
      />

      {editingTagId && (
        <UpdateTagModal
          isOpen={isUpdateOpen}
          onClose={onUpdateClose}
          tagId={editingTagId}
          onTagUpdated={handleTagUpdated}
        />
      )}
    </>
  );
}
